import React, { useState } from 'react';
import axios from 'axios';
import { Phone, MapPin, Mail, ArrowUpRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import usePageMeta from '../hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

const ENGAGEMENTS = [
  'Fractional CIO',
  'Fractional CTO',
  'Engineering Leadership',
  'Program Management',
  'Global Transformation',
  'Interim Leadership',
  'AI Transformation',
  'Not sure yet',
];

export default function Contact() {
  usePageMeta({
    title: 'Contact — Start a Conversation',
    description: 'Reach Waypoint Transformation to discuss fractional technology leadership, program management, or global transformation engagements. Chicago-based; serving clients globally.',
    path: '/contact',
  });
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', engagement: '', message: '',
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast({ title: 'Message received', description: "We'll be in touch within one business day." });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Please try again or call 1-630-447-9380.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#F5F7FA]">
        <Section className="pt-20 pb-14 lg:pt-28 lg:pb-16">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Contact</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-[-0.02em] text-[#0F2A44] max-w-4xl text-balance">
            Tell us where you are. We'll help you get where you're going.
          </h1>
        </Section>
      </section>

      <Section className="py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Info column */}
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Reach us</div>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#0F2A44]/5 flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4 text-[#0F2A44]" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500 mb-1">Phone</div>
                  <a href="tel:16304479380" className="font-serif text-xl text-[#0F2A44] hover:text-[#3E6A94] transition-colors">1-630-447-9380</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#0F2A44]/5 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-[#0F2A44]" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500 mb-1">Email</div>
                  <a href="mailto:info@waypointtransformation.com" className="font-serif text-xl text-[#0F2A44] hover:text-[#3E6A94] transition-colors">info@waypointtransformation.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#0F2A44]/5 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-[#0F2A44]" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500 mb-1">Headquartered in</div>
                  <div className="font-serif text-xl text-[#0F2A44]">Chicago, Illinois</div>
                  <div className="text-sm text-slate-600 mt-1">Serving clients on six continents.</div>
                </div>
              </div>
            </div>

            <div className="mt-14 p-6 bg-[#0F2A44] text-white rounded-sm">
              <div className="font-serif text-xl mb-2">First conversation is on us.</div>
              <p className="text-slate-300 text-sm leading-relaxed">A 30-minute working session to understand where you are and what could move next. No slides, no pressure — just clarity.</p>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div className="border border-slate-200 bg-white p-10 lg:p-14 rounded-sm">
                <CheckCircle2 className="w-10 h-10 text-[#3E6A94] mb-6" />
                <h2 className="font-serif text-3xl lg:text-4xl text-[#0F2A44] mb-4">Thank you — we've got it.</h2>
                <p className="text-slate-600 leading-relaxed max-w-xl">A member of the Waypoint team will reach out within one business day to schedule a working session. In the meantime, feel free to reach us directly at <a href="tel:16304479380" className="text-[#0F2A44] underline">1-630-447-9380</a>.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', company: '', phone: '', engagement: '', message: '' }); }} className="mt-8 inline-flex items-center gap-2 text-sm text-[#0F2A44] border-b border-[#0F2A44]/40 pb-1 hover:border-[#0F2A44]">
                  Send another message <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} required />
                  <Field label="Company" name="company" value={form.company} onChange={onChange} />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-3">Engagement type</label>
                  <div className="flex flex-wrap gap-2">
                    {ENGAGEMENTS.map((e) => (
                      <button
                        type="button"
                        key={e}
                        onClick={() => setForm({ ...form, engagement: e })}
                        className={`px-4 py-2 text-sm border transition-colors rounded-sm ${form.engagement === e ? 'border-[#0F2A44] bg-[#0F2A44] text-white' : 'border-slate-300 text-slate-600 hover:border-[#0F2A44] hover:text-[#0F2A44]'}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-3">How can we help?</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    required
                    className="w-full border border-slate-300 bg-white px-4 py-3 text-[15px] text-[#0F2A44] focus:outline-none focus:border-[#0F2A44] transition-colors rounded-sm"
                    placeholder="A few sentences on where you are and what you're trying to solve."
                  />
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 max-w-sm">By submitting, you agree we may contact you about your inquiry. We'll never share your information.</p>
                  <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-7 py-4 bg-[#0F2A44] text-white text-sm font-medium hover:bg-[#1E3A5F] transition-colors rounded-sm disabled:opacity-60 disabled:cursor-not-allowed">
                    {submitting ? (<>Sending<Loader2 className="w-4 h-4 animate-spin" /></>) : (<>Send message <ArrowUpRight className="w-4 h-4" /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-3">{label}{required && <span className="text-[#3E6A94]"> *</span>}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full border border-slate-300 bg-white px-4 py-3 text-[15px] text-[#0F2A44] focus:outline-none focus:border-[#0F2A44] transition-colors rounded-sm"
      />
    </div>
  );
}
